/* eslint-disable react/no-unused-state */
import { autobind } from 'core-decorators';
import get from 'lodash.get';
import PropTypes from 'prop-types';
import React from 'react';
import _ from 'underscore';
import { processing } from 'app/blocks/common/decorators';
import { compose } from 'app/blocks/common/utils';
import withLightPanel from 'app/blocks/Panel/advanced/withLightPanel';
import Context from 'app/pages/license-signing-process/Payment/GOA/Context';
import { getChinaOaAffiliationName } from 'app/pages/license-signing-process/Payment/utils';
import payment from 'app/pages/orders/orders.payment';
import { ValidationError } from 'app/blocks/common/utils-errortypes';
import validation from 'app/pages/orders/panel/payment/validation';

const PaymentMethod = payment.method;
const methodsWithPONumber = [PaymentMethod.PROFORMA.key, PaymentMethod.INVOICE.key];

const defaultState = {
    paymentDetails: {},
};

function withPaymentPanelContainer(Component) {
    @autobind
    class PaymentPanelContainer extends React.PureComponent {
        static propTypes = {
            canChangePaymentMethod: PropTypes.bool,
            isOrderExisting: PropTypes.bool,
            order: PropTypes.shape({ paymentDetails: PropTypes.shape({}) }).isRequired,
            paymentDetails: PropTypes.shape({}),
            paymentMethods: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
            updatePaymentDetails: PropTypes.func.isRequired,
            updateValidationResults: PropTypes.func.isRequired,
            setValidationHandler: PropTypes.func.isRequired,
        };

        static defaultProps = {
            ...defaultState,
            canChangePaymentMethod: true,
            isOrderExisting: false,
        };

        async componentDidMount() {
            const { order, paymentMethods, setValidationHandler, updatePaymentMethod } = this.props;
            setValidationHandler(this.validate);
            const initialPaymentDetails = { ...order.paymentDetails };
            // When only one payment method is available and it is SocoPay, the radio button is
            // rendered as visually checked. Auto-select it in state so downstream gates
            // (Continue button enablement, china-pay-info static content) reflect that selection.
            if (
                !initialPaymentDetails.paymentMethod &&
                paymentMethods?.length === 1 &&
                paymentMethods[0]?.key === PaymentMethod.SOCOPAY.key
            ) {
                initialPaymentDetails.paymentMethod = PaymentMethod.SOCOPAY.key;
                updatePaymentMethod(PaymentMethod.SOCOPAY.key, true);
            }
            this.updatePayment(initialPaymentDetails);
        }

        @processing('isValidating')
        async validate() {
            const { updateValidationResults } = this.props;
            if (this.props.isChinaOaWithForeignBilling) {
                throw new ValidationError('Billing country must be China for SocoPay payment.', {});
            }

            try {
                await validation(this.props.paymentDetails);
                updateValidationResults(undefined);
            } catch (error) {
                if (error.name === 'ValidationError') {
                    updateValidationResults(error.errors);
                }

                throw error;
            }
        }

        // eslint-disable-next-line react/no-unused-class-component-methods
        isChanged() {
            return Object.keys(defaultState).some(
                key => this.props[key] && !_.isEqual(defaultState[key], this.props[key]),
            );
        }

        updatePayment(newPaymentDetails) {
            const { paymentDetails, updatePaymentDetails } = this.props;
            updatePaymentDetails({ paymentDetails: { ...paymentDetails, ...newPaymentDetails } });
        }

        onPaymentMethodChange(paymentMethod) {
            const { updatePaymentMethod } = this.props;
            const skipPONumber = paymentMethod === PaymentMethod.CREDIT_CARD.key;
            updatePaymentMethod(paymentMethod, skipPONumber);
        }

        onPurchaseNumberChange(poNumber) {
            this.updatePayment({ poNumber });
        }

        onPaymentMethodFocus() {
            const { updateValidationResults, validationResults } = this.props;
            updateValidationResults({ ...validationResults, paymentMethod: undefined });
        }

        render() {
            return (
                <Component
                    {...this.props}
                    isPaymentMethodEditable={this.props.canChangePaymentMethod}
                    isPONumberVisible={methodsWithPONumber.includes(this.props?.paymentDetails?.paymentMethod)}
                    onPaymentMethodChange={this.onPaymentMethodChange}
                    onPaymentMethodFocus={this.onPaymentMethodFocus}
                    onPurchaseNumberChange={this.onPurchaseNumberChange}
                    paymentMethod={get(this.props, 'paymentDetails.paymentMethod')}
                    paymentMethodsAvailable={this.props.paymentMethods}
                    poNumber={get(this.props, 'paymentDetails.poNumber')}
                />
            );
        }
    }

    return PaymentPanelContainer;
}

export { withPaymentPanelContainer };
export default Component =>
    compose(
        Context.withContext(state => {
            const defaultPaymentMethods =
                state.order.billingAddress.countryCode === 'CN' && state.socopayEnabled
                    ? payment.oaPaymentMethodsWithSocopay
                    : payment.oaPaymentMethods;
            return {
                ...state.initiate.payment,
                articleId: state.article?.id,
                canChangePaymentMethod: state.canChangePaymentMethod,
                chinaOaEnabled: state.chinaOaEnabled,
                chinaOaInstitutionName: getChinaOaAffiliationName({ priceProposal: state.order }),
                isChinaOaWithForeignBilling: state.isChinaOaWithForeignBilling,
                isOrderExisting: state.isOrderExisting,
                isPaymentMethodSupportedByBrowser: state.isPaymentMethodSupportedByBrowser,
                paymentDetails: state.paymentDetails,
                paymentMethods: state.chinaOaEnabled ? [payment.method.SOCOPAY] : defaultPaymentMethods,
                register: state.registerPanel,
                updatePaymentDetails: state.updatePaymentDetails,
                updatePaymentMethod: state.updatePaymentMethod,
                updateValidationResults: state.updateValidationResults,
                unregister: state.unregisterPanel,
                order: state.order,
            };
        }),
        withLightPanel('payment'),
        withPaymentPanelContainer,
    )(Component);
